import * as React from "react"

import { cn } from "@/lib/utils"

type InputTransform = 'titleCase' | 'uppercase'

type InputProps = React.ComponentProps<"input"> & {
  transform?: InputTransform
}

function applyTransform(value: string, transform: InputTransform): string {
  if (transform === 'titleCase') {
    return value.toLowerCase().replace(/\b\w/g, c => c.toUpperCase())
  }
  return value.toUpperCase()
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, transform, onChange, value, ...props }, ref) => {
    const innerRef = React.useRef<HTMLInputElement>(null)
    const selRef = React.useRef<number | null>(null)

    const setRef = React.useCallback(
      (el: HTMLInputElement | null) => {
        (innerRef as React.MutableRefObject<HTMLInputElement | null>).current = el
        if (typeof ref === 'function') {
          ref(el)
        } else if (ref) {
          (ref as React.MutableRefObject<HTMLInputElement | null>).current = el
        }
      },
      [ref],
    )

    const handleChange = React.useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!transform || !onChange) {
          onChange?.(e)
          return
        }
        const pos = e.target.selectionStart ?? 0
        const next = applyTransform(e.target.value, transform)
        if (next !== e.target.value) {
          selRef.current = pos
          e.target.value = next
        }
        onChange(e)
      },
      [transform, onChange],
    )

    React.useEffect(() => {
      if (selRef.current !== null && innerRef.current) {
        innerRef.current.setSelectionRange(selRef.current, selRef.current)
        selRef.current = null
      }
    })

    const displayValue =
      transform && typeof value === 'string' ? applyTransform(value, transform) : value

    return (
      <input
        type={type}
        className={cn(
          "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
          className
        )}
        ref={setRef}
        onChange={handleChange}
        value={displayValue}
        {...props}
      />
    )
  }
)
Input.displayName = "Input"

export { Input }
